import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Wand16Regular, Wand20Regular, ArrowClockwise16Regular, Add16Regular } from '@fluentui/react-icons';
import { DrawerBody, OverlayDrawer, Tooltip } from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { FetchIrlCopilotResults, setIrlAiResponse, clearIrlAiResponses } from '../../redux/questionBankSlice';
import { ObservationFormSidebar } from '../Observations';
import styles from './css/index.module.css';
import { swotPrompt, highlightsPrompt, summaryPrompt, keyfindingsPrompt, opinionsPrompt } from './prompts';
import { getRagColor } from '../../utils/commonUtils';

const IRLCoPilotSidebar = ({ selectedRows }) => {
    const [show, setShow] = React.useState(false);
    const { informationRequests, irlCopilotLoading, irlAiResponses } = useSelector(state => state.questionBank);
    const [currentColorIndex, setCurrentColorIndex] = React.useState(0);

    // Get responses from Redux state with proper null checks
    const responseStates = (irlAiResponses && irlAiResponses['saved_analysis']) || {
        highlights: '',
        summary: '',
        keyFindings: '',
        swot: '',
        opinion: '',
    };
    const [showObsSidebar, setShowObsSidebar] = React.useState(false);
    const [selectedText, setSelectedText] = React.useState('');
    const [selectedSignificance, setSelectedSignificance] = React.useState('');
    const [selectedOption, setSelectedOption] = React.useState(null);

    const dispatch = useDispatch();

    // Use ref to track if API call is in progress to prevent loops
    const apiCallInProgress = React.useRef(false);

    const analysisOptions = [
        { id: 'highlights', label: 'Highlights' },
        { id: 'summary', label: 'Summary' },
        { id: 'keyFindings', label: 'Key Findings' },
        { id: 'swot', label: 'SWOT' },
        { id: 'opinion', label: 'Opinion' },
    ];

    const handleOptionSelect = optionId => {
        if (optionId && analysisOptions.find(opt => opt.id === optionId)) {
            setSelectedOption(optionId);
            // Don't clear response - keep existing responses for each option
        }
    };
    const handleFetchData = async () => {
        // Prevent multiple simultaneous API calls
        if (apiCallInProgress.current) {
            return;
        }

        apiCallInProgress.current = true;

        let updatedIds = selectedRows?.filter(id => {
            let irl = informationRequests?.find(item => item?.id === id);
            if (irl?.irresponse) return id;
        });
        let formData = new FormData();
        formData.append('item_ids', `[${updatedIds}]`);
        let prompt = '';
        if (selectedOption === 'swot') {
            prompt = swotPrompt;
        } else if (selectedOption === 'highlights') {
            prompt = highlightsPrompt;
        } else if (selectedOption === 'summary') {
            prompt = summaryPrompt;
        } else if (selectedOption === 'keyFindings') {
            prompt = keyfindingsPrompt;
        } else if (selectedOption === 'opinion') {
            prompt = opinionsPrompt;
        }
        formData.append('task', prompt);
        formData.append('analysis', selectedOption); // Explicitly pass analysis type

        try {
            let res = await dispatch(
                FetchIrlCopilotResults({
                    body: formData,
                    analysisType: selectedOption, // Pass analysis type for Redux reducer
                }),
            );
            console.log(res);
        } catch (error) {
            console.error('API call failed:', error);
        } finally {
            apiCallInProgress.current = false;
        }
    };
    const handleCreateObservation = (obs, swot) => {
        let observationText = '';
        let significanceText = '';

        if (swot === 's' || swot === 'o') {
            // Format for Strengths and Opportunities - Plain text
            observationText = `${obs.headline || ''}\n\n${obs.description || ''}`;
            significanceText = obs.valueInsight || '';
        } else {
            // Format for Weaknesses and Threats - Plain text
            observationText = `${obs.headline || ''}\n\n${obs.description || ''}`;
            significanceText = obs.mitigationStrategy || '';
        }

        setSelectedText(observationText);
        setSelectedSignificance(significanceText);
        setShowObsSidebar(true);
    };
    React.useEffect(() => {
        if (selectedOption && !responseStates[selectedOption] && !irlCopilotLoading && !apiCallInProgress.current) {
            //Run API call to fetch co pilot data only if response doesn't exist and not already loading
            handleFetchData();
        }
    }, [selectedOption]); // Remove selectedRowsKey since we use 'saved_analysis' key now

    // Removed cleanup useEffect to prevent infinite loops
    // The Redux state will handle memory management automatically

    React.useEffect(() => {
        let intervalId;

        if (irlCopilotLoading) {
            // Start cycling through colors when loading is true
            intervalId = setInterval(() => {
                setCurrentColorIndex(prevIndex => (prevIndex === 2 ? 0 : prevIndex + 1));
            }, 1000); // Change color every 1 second (adjust as needed)
        } else {
            // Reset color index when loading is false
            setCurrentColorIndex(0);
            clearInterval(intervalId); // Clear interval when loading becomes false
        }

        return () => clearInterval(intervalId); // Cleanup interval on component unmount
    }, [irlCopilotLoading]);

    const colors = ['#8E2DE2', '#F8047E', '#192BC2'];
    const currentColor = colors[currentColorIndex];
    return (
        <>
            <button onClick={() => setShow(true)} type="button" className={`btn btn-primary btn-sm add-irl-button me-2 d-flex align-items-center ${styles.smallButton}`}>
                <Wand16Regular className="me-1" /> Lens AI Analysis
            </button>
            <OverlayDrawer
                style={{ minWidth: 872 }}
                type="overlay"
                position="end"
                open={show}
                onOpenChange={(_, { open }) => {
                    setShow(open);
                    if (!open) {
                        setSelectedOption(null);
                        // Don't clear response states - preserve them in Redux
                    }
                }}>
                <DrawerBody className={`position-relative ${styles.drawerContent}`} style={{ padding: 0 }}>
                    <div className="d-flex align-items-center justify-content-between px-3 mt-3">
                        <div className="d-flex align-items-center">
                            <Wand20Regular color="var(--Brand-Secondary)" className="me-1" /> <span className={styles.aiTitle}>Lens AI Analysis</span>
                        </div>
                        <Dismiss20Regular
                            onClick={() => {
                                setShow(false);
                                setSelectedOption(null);
                                // Don't clear response states - preserve them in Redux
                            }}
                            role="button"
                            color="var(--Neutral-Gray-6)"
                        />
                    </div>

                    <div className={styles.analysisOptions}>
                        <div>
                            {analysisOptions?.map(item => (
                                <span
                                    key={item.id}
                                    onClick={() => !irlCopilotLoading && handleOptionSelect(item?.id)}
                                    style={{
                                        color: selectedOption !== item?.id ? 'var(--Brand-Secondary)' : 'var(--Brand-Primary)',
                                        opacity: irlCopilotLoading ? 0.5 : 1,
                                        cursor: irlCopilotLoading ? 'not-allowed' : 'pointer',
                                    }}
                                    className={styles.analysisOption}>
                                    {item?.label}
                                </span>
                            ))}
                        </div>
                        {!irlCopilotLoading && (
                            <div
                                onClick={() => {
                                    if (selectedOption) {
                                        // Clear the current response for the selected option and refetch
                                        dispatch(
                                            setIrlAiResponse({
                                                selectedRowsKey: 'saved_analysis',
                                                analysisType: selectedOption,
                                                response: '',
                                            }),
                                        );
                                        handleFetchData();
                                    }
                                }}
                                style={{ color: selectedOption ? 'var(--Brand-Secondary)' : 'var(--Neutral-Gray-6)' }}
                                className="d-flex align-items-center ms-4"
                                role="button">
                                <ArrowClockwise16Regular className="me-1" />
                                <span className={styles?.regenerateText}>Regenerate</span>
                            </div>
                        )}
                    </div>
                    {!selectedOption && <div className={styles.noOptionSelected}>Please choose an option to proceed.</div>}
                    {irlCopilotLoading && (
                        <div className={styles.copilotLoader}>
                            <Wand20Regular
                                style={{
                                    transition: 'color 0.3s ease', // Add transition for color change
                                    color: irlCopilotLoading ? currentColor : 'inherit', // Change color based on loading state
                                }}
                            />
                            <p>Loading results</p>
                        </div>
                    )}

                    {!irlCopilotLoading && selectedOption === 'swot' && <SWOTResponse handleCreateObservation={handleCreateObservation} response={responseStates.swot} />}
                    {!irlCopilotLoading && selectedOption === 'highlights' && <HighlightsResponse response={responseStates.highlights} />}
                    {!irlCopilotLoading && selectedOption === 'summary' && <SummaryResponse response={responseStates.summary} />}
                    {!irlCopilotLoading && selectedOption === 'keyFindings' && <KeyFindingsResponse response={responseStates.keyFindings} />}
                    {!irlCopilotLoading && selectedOption === 'opinion' && <OpinionResponse response={responseStates.opinion} />}
                </DrawerBody>
            </OverlayDrawer>
            <ObservationFormSidebar
                show={showObsSidebar}
                setShow={setShowObsSidebar}
                setDefaultData={setSelectedText}
                defaultObservation={selectedText}
                defaultSignificance={selectedSignificance}
            />
        </>
    );
};

const HighlightsResponse = ({ response }) => {
    return (
        <div>
            <div className={styles.highlightsResponseContent}>
                <div className={styles.grayHeadlineResponse}>Team</div>
                <p className={styles.primaryBoldTextResponse + ' mb-2'}>{response?.highlights?.Team?.headline}</p>
                <p className={styles.regularTextResponse}>{response?.highlights?.Team?.body}</p>
            </div>
            <div className={styles.highlightsResponseContent}>
                <div className={styles.grayHeadlineResponse}>Cybersecurity</div>
                <p className={styles.primaryBoldTextResponse + ' mb-2'}>{response?.highlights?.Cybersecurity?.headline}</p>
                <p className={styles.regularTextResponse}>{response?.highlights?.Cybersecurity?.body}</p>
            </div>
            <div className={styles.highlightsResponseContent}>
                <div className={styles.grayHeadlineResponse}>Technology</div>
                <p className={styles.primaryBoldTextResponse + ' mb-2'}>{response?.highlights?.Technology?.headline}</p>
                <p className={styles.regularTextResponse}>{response?.highlights?.Technology?.body}</p>
            </div>
        </div>
    );
};
const SummaryResponse = ({ response }) => {
    return (
        <div>
            <div className={styles.highlightsResponseContent}>
                <div className={styles.grayHeadlineResponse}>Management Summary</div>
                <p className={styles.primaryBoldTextResponse + ' mb-4'}>{response?.summary?.headline}</p>
                <div className={styles.grayHeadlineResponse}>Overview</div>
                <p className={styles.regularTextResponse}>{response?.summary?.overview}</p>
                <br />
                <div className="d-flex ">
                    <div className={styles.grayHeadlineResponse}>Overall Result</div>
                    <div
                        style={{
                            backgroundColor: getRagColor(response?.summary?.rag),
                        }}
                        className={styles.roundBadge}></div>
                </div>
                <p className={styles.regularTextResponse}>{response?.summary?.overallResult}</p>
            </div>
        </div>
    );
};
const KeyFindingsResponse = ({ response }) => {
    return (
        <div>
            {response?.keyfindings?.map(item => (
                <div key={item?.scope} className={styles.KFResponseContent}>
                    <div className="d-flex">
                        <div className={styles.grayHeadlineResponse}>{item?.scope}</div>
                        <div
                            style={{
                                backgroundColor: getRagColor(item?.rag),
                            }}
                            className={styles.roundBadge}></div>
                    </div>
                    <p className={styles.regularTextResponse + ' mb-2'}>{item?.headline}</p>
                </div>
            ))}
        </div>
    );
};
const SWOTResponse = ({ response, handleCreateObservation }) => {
    return (
        <div className={styles.swotResponseContent}>
            <p className={styles.grayHeadlineResponse}>Strengths</p>
            {response?.swot?.Strengths?.map(item => (
                <div key={item?.headline} className="row">
                    <div className="col-sm-1 pe-0">
                        <Add16Regular color="var(--Brand-Secondary)" onClick={() => handleCreateObservation(item, 's')} />
                    </div>
                    <div className="col-sm-10  pe-0">
                        <p className={styles.swotResponseLabel}>{item?.headline}</p>
                        <p className={styles.regularTextResponse + ' mb-0'}>{item?.description}</p>
                        <p className={styles.swotGrayLabel}>Comment</p>
                        <p className={styles.regularTextResponse}>{item?.valueInsight}</p>
                    </div>
                </div>
            ))}
            <p className={styles.grayHeadlineResponse + ' mt-3'}>Weaknesses</p>
            {response?.swot?.Weaknesses?.map(item => (
                <div key={item?.headline} className="row">
                    <div className="col-sm-1 pe-0">
                        <Add16Regular color="var(--Brand-Secondary)" onClick={() => handleCreateObservation(item, 'w')} />
                    </div>
                    <div className="col-sm-10  pe-0">
                        <p className={styles.swotResponseLabel}>{item?.headline}</p>
                        <p className={styles.regularTextResponse + ' mb-0'}>{item?.description}</p>
                        <p className={styles.swotGrayLabel}>Mitigation</p>
                        <p className={styles.regularTextResponse}>{item?.mitigationStrategy}</p>
                    </div>
                    <div className="col-sm-1 pe-0">
                        <div className={styles.ragBadge} style={{ backgroundColor: getRagColor(item?.rag) }}></div>
                    </div>
                </div>
            ))}
            <p className={styles.grayHeadlineResponse + ' mt-3'}>Opportunities</p>
            {response?.swot?.Opportunities?.map(item => (
                <div key={item?.headline} className="row">
                    <div className="col-sm-1 pe-0">
                        <Add16Regular color="var(--Brand-Secondary)" onClick={() => handleCreateObservation(item, 'o')} />
                    </div>
                    <div className="col-sm-10  pe-0">
                        <p className={styles.swotResponseLabel}>{item?.headline}</p>
                        <p className={styles.regularTextResponse + ' mb-0'}>{item?.description}</p>
                        <p className={styles.swotGrayLabel}>Comment</p>
                        <p className={styles.regularTextResponse}>{item?.valueInsight}</p>
                    </div>
                </div>
            ))}
            <p className={styles.grayHeadlineResponse + ' mt-3'}>Threats</p>
            {response?.swot?.Threats?.map(item => (
                <div key={item?.headline} className="row">
                    <div className="col-sm-1 pe-0">
                        <Add16Regular color="var(--Brand-Secondary)" onClick={() => handleCreateObservation(item, 't')} />
                    </div>
                    <div className="col-sm-10  pe-0">
                        <p className={styles.swotResponseLabel}>{item?.headline}</p>
                        <p className={styles.regularTextResponse + ' mb-0'}>{item?.description}</p>
                        <p className={styles.swotGrayLabel}>Mitigation</p>
                        <p className={styles.regularTextResponse}>{item?.mitigationStrategy}</p>
                    </div>
                    <div className="col-sm-1 pe-0">
                        <div className={styles.ragBadge} style={{ backgroundColor: getRagColor(item?.rag) }}></div>
                    </div>
                </div>
            ))}
        </div>
    );
};
const OpinionResponse = ({ response }) => {
    return (
        <div>
            {response?.opinion?.map((item, index) => (
                <div key={index} className={styles.highlightsResponseContent}>
                    <p className={styles.grayHeadlineResponse}>{item?.question}</p>
                    <p className={styles.regularTextResponse}>{item?.answer}</p>
                </div>
            ))}
        </div>
    );
};

export default IRLCoPilotSidebar;
